import React from 'react'
import PropTypes from 'prop-types'
import { Dropdown, Form, Icon } from 'semantic-ui-react'
import SkillList from './SkillList'

/**
 * Renders the content of a single skill tab: optional add-dropdown section,
 * the skill list, and the legend text.
 *
 * Props:
 *   editable          {boolean}   Whether the user can add/edit/delete
 *   items             {Array}     Normalized items for SkillList ({ id, name, level })
 *   options           {Array}     Filtered dropdown options (already diffed)
 *   placeholder       {string}    Placeholder text for the add-dropdown
 *   legend            {string}    Legend text explaining levels
 *   onDropdownSelect  {Function}  Callback when a new item is selected from dropdown. fn(e, data)
 *   onLevelChange     {Function}  Callback when rating changes. fn(id, newLevel)
 *   onDelete          {Function}  Callback when item is deleted. fn(id)
 *   categorySection   {ReactNode} [optional] Additional section rendered before the add-dropdown (e.g. category selector)
 *   tabId             {string}    Unique id for accessibility (aria-labelledby)
 *   panelId           {string}    Unique id for the tabpanel element (linked via aria-controls on the tab)
 */
const SkillTabContent = ({
  editable,
  items,
  options,
  placeholder,
  legend,
  onDropdownSelect,
  onLevelChange,
  onDelete,
  categorySection,
  tabId,
  panelId,
}) => {
  return (
    <div
      id={panelId}
      className="skills-tab-content"
      role="tabpanel"
      aria-labelledby={tabId}
    >
      {editable && (
        <div className="skills-tab-add-section">
          <Form>
            {categorySection && categorySection}
            <Form.Field>
              <Dropdown
                placeholder={placeholder}
                search
                selection
                options={options}
                onChange={onDropdownSelect}
                value=""
                className="skills-tab-dropdown"
              />
            </Form.Field>
          </Form>
        </div>
      )}
      <SkillList
        items={items}
        editable={editable}
        onLevelChange={onLevelChange}
        onDelete={onDelete}
        aria-describedby={panelId + '-legend'}
      />
      <div
        id={panelId + '-legend'}
        className="skills-tab-legend"
        role="note"
      >
        <Icon name="info circle" size="small" aria-hidden="true" />
        {legend}
      </div>
    </div>
  )
}

SkillTabContent.propTypes = {
  editable: PropTypes.bool.isRequired,
  items: PropTypes.array.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string.isRequired,
  legend: PropTypes.string.isRequired,
  onDropdownSelect: PropTypes.func.isRequired,
  onLevelChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  categorySection: PropTypes.node,
  tabId: PropTypes.string,
  panelId: PropTypes.string,
}

export default SkillTabContent
